import React, { FC, useMemo } from 'react';
import { Box, Typography, Chip, Avatar, CircularProgress } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { UserByCategoryItem } from '../../../../apis/users/users.api.interfaces';
import { getInitials, getFullName, formatDate } from '../users.helpers';
import { getStatusColor, getStatusLabel } from '../users.helpers';
import { UserStatus } from '../users.interfaces';
import PaginatedGrid from '../../../components/paginatedGrid/PaginatedGrid';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface CategoryUsersViewProps {
  users: UserByCategoryItem[];
  isLoading?: boolean;
  onUserSelect: (user: UserByCategoryItem) => void;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
}

const CategoryUsersView: FC<CategoryUsersViewProps> = ({
  users,
  isLoading,
  onUserSelect,
  total,
  page,
  pageSize,
  onPageChange,
}) => {
  const { translateText } = useTranslation();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: translateText('users.list.columns.name'),
        flex: 1,
        minWidth: 180,
        renderCell: (params: any) => {
          const user = params.row as UserByCategoryItem;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              <Avatar
                src={user.profilePicture}
                sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}
              >
                {getInitials(user.firstName, user.lastName)}
              </Avatar>
              <Typography variant="body2" fontWeight="medium">
                {getFullName(user.firstName, user.lastName)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'userRole',
        headerName: translateText('users.list.columns.role'),
        width: 150,
        renderCell: (params: any) => (
          <Chip
            label={translateText(`users.options.roles.${params.value}`) || params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
      {
        field: 'email',
        headerName: translateText('users.list.columns.email'),
        flex: 1,
        minWidth: 200,
      },
      // {
      //   field: 'phone',
      //   headerName: translateText('users.list.columns.phone'),
      //   width: 140,
      // },
      {
        field: 'userStatus',
        headerName: translateText('users.list.columns.status'),
        width: 120,
        renderCell: (params: any) => (
          <Chip
            label={getStatusLabel(params.value as UserStatus)}
            size="small"
            sx={{
              bgcolor: getStatusColor(params.value as UserStatus),
              color: '#fff',
            }}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: translateText('users.list.columns.created'),
        width: 120,
        renderCell: (params: any) => (
          <Typography variant="caption">
            {params.value ? formatDate(params.value) : '—'}
          </Typography>
        ),
      },
    ],
    [translateText]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} data-testid="category-users-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }} data-testid="category-users-grid">
      <PaginatedGrid
        rows={users}
        columns={columns}
        onRowClick={(params: any) => onUserSelect(params.row)}
        getRowId={(row: any) => row.id}
        autoHeight
        disableRowSelectionOnClick
        serverSide={!!onPageChange}
        totalCount={total}
        currentPage={page}
        currentPageSize={pageSize}
        onPageChange={onPageChange}
        data-testid="category-users-grid-table"
      />
    </Box>
  );
};

export default CategoryUsersView;
